import { Button, Flex, Text } from "@radix-ui/themes";

import {
  DataTable,
  type DataTableColumns,
  type DataTableDensity,
} from "~/v3-poc/components/DataTable";
import { isOrdersWidgetColumnId } from "./OrdersWidget.columns";
import type { OrdersWidgetRow } from "./OrdersWidget.types";
import { ORDERS_UNAVAILABLE_VALUE } from "./OrdersWidget.utils";
import type { ReorderOrdersColumns } from "./useOrdersColumnVisibility";

const OrdersResultsEmptyFilters = ({
  message,
  onReset,
}: {
  message: string;
  onReset: () => void;
}) => (
  <Flex align="center" justify="between" gap="2" wrap="wrap">
    <Text size="2" color="gray">
      {message}
    </Text>
    <Button
      type="button"
      size="1"
      variant="soft"
      color="gray"
      onClick={onReset}
    >
      Reset filters
    </Button>
  </Flex>
);

export interface OrdersResultsTablePresentationProps {
  rows: OrdersWidgetRow[];
  columns: DataTableColumns<OrdersWidgetRow>;
  tableMinWidth: string;
  density: DataTableDensity;
  horizontalLines: boolean;
  verticalLines: boolean;
  selectedOrderId?: string;
  batchSelectedIds: readonly string[];
  isAccountReady: boolean;
  hasAppliedFilters: boolean;
  emptyMessage: string;
  onReset: () => void;
  onRowActivate: (
    row: OrdersWidgetRow,
    rowElement: HTMLTableRowElement,
  ) => void;
  onReorderColumns: ReorderOrdersColumns;
  onViewportScroll?: (event: Event) => void;
}

export const OrdersResultsTablePresentation = ({
  rows,
  columns,
  tableMinWidth,
  density,
  horizontalLines,
  verticalLines,
  selectedOrderId,
  batchSelectedIds,
  isAccountReady,
  hasAppliedFilters,
  emptyMessage,
  onReset,
  onRowActivate,
  onReorderColumns,
  onViewportScroll,
}: OrdersResultsTablePresentationProps) => {
  const batchSelectedIdSet = new Set(batchSelectedIds);
  const isRowSelected =
    batchSelectedIds.length > 0
      ? (row: OrdersWidgetRow) =>
          batchSelectedIdSet.has(row.id) ||
          batchSelectedIdSet.has(row.parentOrderId)
      : undefined;
  const isRowActive = selectedOrderId
    ? (row: OrdersWidgetRow) =>
        row.id === selectedOrderId || row.parentOrderId === selectedOrderId
    : undefined;
  const emptyContent =
    isAccountReady && hasAppliedFilters ? (
      <OrdersResultsEmptyFilters message={emptyMessage} onReset={onReset} />
    ) : undefined;

  return (
    <DataTable
      columns={columns}
      data={rows}
      variant="ghost"
      size="1"
      caption="Orders"
      className="orders-widget-table"
      contentMinWidth={tableMinWidth}
      emptyMessage={emptyMessage}
      density={density}
      horizontalLines={horizontalLines}
      verticalLines={verticalLines}
      showIdleSortIndicator
      onRowActivate={onRowActivate}
      getRowAriaLabel={(row) =>
        `View details for ${row.symbol} order ${
          row.isLegRow ? row.parentOrderId : row.id
        }`
      }
      isRowActivatable={(row) => row.parentOrderId !== ORDERS_UNAVAILABLE_VALUE}
      isRowSelected={isRowSelected}
      isRowActive={isRowActive}
      emptyContent={emptyContent}
      getRowId={(row) => row.id}
      getRowGroupId={(row) => (row.isLegRow ? row.parentOrderId : row.id)}
      onColumnReorder={({ sourceColumnId, targetColumnId, direction }) => {
        if (
          !isOrdersWidgetColumnId(sourceColumnId) ||
          !isOrdersWidgetColumnId(targetColumnId)
        ) {
          return;
        }
        onReorderColumns(sourceColumnId, targetColumnId, direction);
      }}
      onViewportScroll={onViewportScroll}
    />
  );
};
